import { useEffect, useState } from 'react'
import type { AccountPlan, AccountProfile, AccountPromotionStatus, AppStatus, CurrentAccount, CurrentLegalDocuments } from '../types'
import { ApiError, api } from '../lib/api'
import { displayNameUpdatePayload } from '../lib/accountProfileScope'
import { legalConsentDecisions, legalDocumentsForEnrollment, legalDocumentsUsable } from '../lib/legalConsent'
import { CentralLegalChecklist } from './CentralLegalChecklist'
import { userMessageForError } from '../lib/userError'
import { useI18n } from '../i18n/useI18n'
import { toMemberLegalLocale } from '../lib/legalLocale'
import {
  accountNoticeForTab,
  clearAccountNotice,
  clearAllAccountNotices,
  setAccountNotice,
  type AccountNoticeState,
  type AccountNoticeTone,
  type AccountTab,
} from '../lib/accountNotice'

interface AccountPanelProps {
  account: CurrentAccount
  status: AppStatus | null
  plan: AccountPlan | null
  onLogout: () => Promise<void>
  onAccountChanged: (account: CurrentAccount) => void
  onPasswordChanged: (message: string) => void
}

function currentAccountFromProfile(profile: AccountProfile): CurrentAccount {
  return {
    userId: profile.userId,
    username: profile.username,
    displayName: profile.displayName,
    authVersion: profile.authVersion,
    memberStatus: profile.memberStatus,
    serviceStatus: profile.serviceStatus,
    termsReconsentRequired: profile.termsReconsentRequired,
  }
}

function errorMessage(error: unknown, fallback: string): string {
  return userMessageForError(error, fallback)
}

function legalReviewErrorMessage(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    if (error.code === 'LEGAL_CONTRACT_CHANGED') {
      return t('account.legal.changed')
    }
    if (error.code === 'INVALID_CREDENTIALS' || error.code === 'PASSWORD_INVALID') {
      return t('account.legal.passwordInvalid')
    }
    if (error.code === 'FINAL_LEGAL_DOCUMENTS_NOT_READY') {
      return t('account.legal.notReady')
    }
  }
  return t('account.error.legalReview')
}

export const ACCOUNT_PANEL_STATE_VERSION = '0.10.0'

export function AccountPanel({
  account,
  status,
  plan,
  onLogout,
  onAccountChanged,
  onPasswordChanged,
}: AccountPanelProps) {
  const { locale, t } = useI18n()
  const legalLocale = toMemberLegalLocale(locale)
  const [tab, setTab] = useState<AccountTab>('summary')
  const [profile, setProfile] = useState<AccountProfile | null>(null)
  const [loading, setLoading] = useState(true)

  // 처리 중인 탭과 안내 문구를 탭별로 분리합니다. 이렇게 해야 공통 프로필의
  // 검증 오류가 복구 이메일 화면에 남는 식의 상태 혼재가 발생하지 않습니다.
  const [pendingTab, setPendingTab] = useState<AccountTab | null>(null)
  const [notices, setNotices] = useState<AccountNoticeState>({})
  const [legalDocuments, setLegalDocuments] = useState<CurrentLegalDocuments | null>(null)
  const [legalLoading, setLegalLoading] = useState(false)
  const [legalError, setLegalError] = useState('')
  const [legalRefreshNonce, setLegalRefreshNonce] = useState(0)
  const [legalSubmitting, setLegalSubmitting] = useState(false)
  const [promotion, setPromotion] = useState<AccountPromotionStatus | null>(null)
  const [promotionPlan, setPromotionPlan] = useState<AccountPlan | null>(null)
  const [promotionLoading, setPromotionLoading] = useState(true)
  const [promotionSubmitting, setPromotionSubmitting] = useState(false)
  const [promotionMessage, setPromotionMessage] = useState('')

  const legalReviewRequired = profile?.termsReconsentRequired ?? account.termsReconsentRequired

  function showNotice(targetTab: AccountTab, text: string, tone: AccountNoticeTone) {
    setNotices((current) => setAccountNotice(current, targetTab, text, tone))
  }

  function clearNotice(targetTab: AccountTab) {
    setNotices((current) => clearAccountNotice(current, targetTab))
  }

  /** 탭을 바꿀 때 이전 탭의 성공·오류 안내를 모두 지워 오래된 문구가 따라오지 않게 합니다. */
  function switchTab(nextTab: AccountTab) {
    setNotices(clearAllAccountNotices())
    setTab(nextTab)
  }

  function renderNotice(targetTab: AccountTab) {
    const notice = accountNoticeForTab(notices, targetTab)
    if (!notice) return null
    return <p className={`notice ${notice.tone}`} role="status">{notice.text}</p>
  }

  useEffect(() => {
    let active = true
    void api.accountProfile()
      .then((nextProfile) => {
        if (!active) return
        setProfile(nextProfile)
        onAccountChanged(currentAccountFromProfile(nextProfile))
      })
      .catch((error) => {
        if (active) showNotice('summary', errorMessage(error, t('account.error.load')), 'error')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => { active = false }
  }, [])

  useEffect(() => {
    let active = true
    setPromotionLoading(true)
    void api.accountPromotion()
      .then((next) => {
        if (active) setPromotion(next)
      })
      .catch(() => {
        if (active) setPromotionMessage(t('account.promotion.loadFailed'))
      })
      .finally(() => {
        if (active) setPromotionLoading(false)
      })
    return () => { active = false }
  }, [])

  async function redeemPromotion(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const formElement = event.currentTarget
    const form = new FormData(formElement)
    const code = String(form.get('promotionCode') ?? '').trim()
    if (!code) {
      setPromotionMessage(t('account.promotion.codeRequired'))
      return
    }

    setPromotionSubmitting(true)
    setPromotionMessage('')
    try {
      const result = await api.redeemPromotion(code)
      setPromotion(result.promotion)
      setPromotionPlan(result.plan)
      formElement.reset()
      setPromotionMessage(t('account.promotion.applied'))
    } catch (error) {
      setPromotionMessage(userMessageForError(error, t('account.promotion.applyFailed')))
    } finally {
      setPromotionSubmitting(false)
    }
  }

  useEffect(() => {
    if (!legalReviewRequired) {
      setLegalDocuments(null)
      setLegalError('')
      return
    }

    let active = true
    setLegalLoading(true)
    setLegalError('')
    void api.currentLegalDocuments(legalLocale)
      .then((documents) => {
        if (active) setLegalDocuments(documents)
      })
      .catch(() => {
        if (!active) return
        setLegalDocuments(null)
        setLegalError(t('account.error.legalLoad'))
      })
      .finally(() => {
        if (active) setLegalLoading(false)
      })

    return () => { active = false }
  }, [legalReviewRequired, legalLocale, legalRefreshNonce])

  function legalReviewDocuments() {
    if (!legalDocuments) return []
    // 현재 호환 계약의 termsReconsentRequired는 item별 action을 제공하지 않습니다.
    // 앱이 어떤 문서가 최신/재동의 대상인지 추론하지 않고, 중앙이 준 current 문서를 모두 표시합니다.
    return legalDocumentsForEnrollment(legalDocuments, true)
  }

  function legalReviewReady(): boolean {
    return !legalLoading && !legalError && legalDocumentsUsable(legalReviewDocuments())
  }

  async function submitLegalReview(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const documents = legalReviewDocuments()
    if (!legalReviewReady()) {
      showNotice('summary', legalError || t('account.legal.reviewCurrent'), 'error')
      return
    }

    setLegalSubmitting(true)
    clearNotice('summary')
    try {
      const nextAccount = await api.serviceEnrollment({
        username: account.username,
        password: String(form.get('password') ?? ''),
        legalLocale: legalDocuments!.requestedLocale,
        legalConsents: legalConsentDecisions(form, documents),
      })
      onAccountChanged(nextAccount)
      const nextProfile = await api.accountProfile()
      setProfile(nextProfile)
      onAccountChanged(currentAccountFromProfile(nextProfile))
      if (nextProfile.termsReconsentRequired) {
        setLegalRefreshNonce((value) => value + 1)
        showNotice('summary', t('account.legal.additionalReview'), 'error')
      } else {
        setLegalDocuments(null)
        showNotice('summary', t('account.legal.completed'), 'success')
      }
    } catch (error) {
      if (error instanceof ApiError && error.code === 'LEGAL_CONTRACT_CHANGED') {
        setLegalDocuments(null)
        setLegalRefreshNonce((value) => value + 1)
        showNotice('summary', t('account.legal.changed'), 'error')
      } else {
        showNotice('summary', legalReviewErrorMessage(error, t), 'error')
      }
    } finally {
      setLegalSubmitting(false)
    }
  }

  async function saveProfile(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setPendingTab('profile')
    clearNotice('profile')
    try {
      // mioSegno는 중앙회원의 전화번호·생일·달력 구분을 수집하거나 수정하지 않습니다.
      // 표시 이름만 전송해야 중앙에 이미 저장된 다른 공통 필드가 그대로 보존됩니다.
      const nextProfile = await api.updateAccountProfile(
        displayNameUpdatePayload(String(form.get('displayName') ?? '')),
      )
      setProfile(nextProfile)
      onAccountChanged(currentAccountFromProfile(nextProfile))
      showNotice('profile', t('account.message.profileUpdated'), 'success')
    } catch (error) {
      showNotice('profile', errorMessage(error, t('account.error.profileUpdate')), 'error')
    } finally {
      setPendingTab(null)
    }
  }

  async function changePassword(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const formElement = event.currentTarget
    const form = new FormData(formElement)
    setPendingTab('password')
    clearNotice('password')
    try {
      await api.changeAccountPassword({
        currentPassword: String(form.get('currentPassword') ?? ''),
        newPassword: String(form.get('newPassword') ?? ''),
        newPasswordConfirm: String(form.get('newPasswordConfirm') ?? ''),
      })
      formElement.reset()
      onPasswordChanged(t('account.message.passwordChanged'))
    } catch (error) {
      showNotice('password', errorMessage(error, t('account.error.passwordChange')), 'error')
    } finally {
      setPendingTab(null)
    }
  }

  async function updateRecoveryEmail(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const formElement = event.currentTarget
    const form = new FormData(formElement)
    setPendingTab('recoveryEmail')
    clearNotice('recoveryEmail')
    try {
      const nextProfile = await api.updateRecoveryEmail(
        String(form.get('currentPassword') ?? ''),
        String(form.get('recoveryEmail') ?? ''),
      )
      formElement.reset()
      setProfile(nextProfile)
      onAccountChanged(currentAccountFromProfile(nextProfile))
      showNotice('recoveryEmail', t('account.message.recoveryEmailChanged'), 'success')
    } catch (error) {
      showNotice('recoveryEmail', errorMessage(error, t('account.error.recoveryEmail')), 'error')
    } finally {
      setPendingTab(null)
    }
  }

  async function resendVerification() {
    setPendingTab('recoveryEmail')
    clearNotice('recoveryEmail')
    try {
      await api.requestRecoveryEmailVerification()
      showNotice('recoveryEmail', t('account.message.verificationRequested'), 'success')
    } catch (error) {
      showNotice('recoveryEmail', errorMessage(error, t('account.error.recoveryEmail')), 'error')
    } finally {
      setPendingTab(null)
    }
  }

  const shownProfile: AccountProfile = profile ?? {
    userId: account.userId,
    username: account.username,
    displayName: account.displayName,
    recoveryEmailMasked: '',
    recoveryEmailVerified: false,
    authVersion: account.authVersion,
    memberStatus: account.memberStatus,
    serviceStatus: account.serviceStatus,
    termsReconsentRequired: account.termsReconsentRequired,
  }

  const shownPlan = promotionPlan ?? plan
  const requestInProgress = pendingTab !== null

  return (
    <section className="card account-card account-settings-card" data-account-ui-version={ACCOUNT_PANEL_STATE_VERSION}>
      <div className="section-title-row">
        <div>
          <p className="eyebrow">{t('account.title')}</p>
          <h2>{shownProfile.displayName || shownProfile.username}</h2>
        </div>
      </div>

      <div className="tab-row" aria-label={t('account.tabs.ariaLabel')}>
        <button type="button" disabled={requestInProgress} className={tab === 'summary' ? 'tab active' : 'tab'} onClick={() => switchTab('summary')}>{t('account.tabs.summary')}</button>
        <button type="button" disabled={requestInProgress} className={tab === 'profile' ? 'tab active' : 'tab'} onClick={() => switchTab('profile')}>{t('account.tabs.profile')}</button>
        <button type="button" disabled={requestInProgress} className={tab === 'password' ? 'tab active' : 'tab'} onClick={() => switchTab('password')}>{t('account.tabs.password')}</button>
        <button type="button" disabled={requestInProgress} className={tab === 'recoveryEmail' ? 'tab active' : 'tab'} onClick={() => switchTab('recoveryEmail')}>{t('account.tabs.recoveryEmail')}</button>
      </div>

      {loading && <p className="muted">{t('account.loading')}</p>}

      {tab === 'summary' && (
        <>
          <section className="account-plan-card" id="account-plan">
            <div>
              <p className="eyebrow">{t('account.plan.current')}</p>
              <h3>{shownPlan ? t(`account.plan.name.${shownPlan.code}`, {}, shownPlan.code) : t('account.plan.checking')}</h3>
            </div>
            {shownPlan && (
              <div className="account-plan-features">
                <span>{t('account.plan.dataRetentionPolicy')}</span>
                <span>{shownPlan.sharingEnabled
                  ? (shownPlan.activeShareLimitConfigured
                    ? t('account.plan.activeShares', { used: shownPlan.activeShareCount, limit: shownPlan.activeShareLimit ?? 0 })
                    : t('account.plan.operatorSetupRequired'))
                  : t('account.plan.sharingUnavailable')}</span>
                {shownPlan.weeklyFileRegistrationLimit != null && (
                  <span>{shownPlan.fileRegistrationWindowDays != null
                    ? t('account.plan.fileRegistrationWindow', {
                      days: shownPlan.fileRegistrationWindowDays,
                      used: shownPlan.weeklyFileRegistrationsUsed,
                      limit: shownPlan.weeklyFileRegistrationLimit,
                    })
                    : t('account.plan.fileRegistrationPeriod', {
                      used: shownPlan.weeklyFileRegistrationsUsed,
                      limit: shownPlan.weeklyFileRegistrationLimit,
                    })}</span>
                )}
                {shownPlan.sharingEnabled && <span>{t('account.plan.shareRetentionPolicy')}</span>}
                {shownPlan.albumSharingEnabled && <span>{t('account.plan.albumSharing')}</span>}
              </div>
            )}
            <section className="account-promotion-panel" aria-label={t('account.promotion.title')}>
              <p className="eyebrow">{t('account.promotion.title')}</p>
              {promotionLoading && <p className="muted">{t('account.promotion.loading')}</p>}
              {!promotionLoading && promotion?.active && (
                <div className="account-plan-features">
                  <strong>{t('account.promotion.active', { name: promotion.name ?? promotion.code ?? '' })}</strong>
                  {promotion.benefitExpiresAt && <span>{t('account.promotion.expiresAt', { date: new Date(promotion.benefitExpiresAt).toLocaleDateString() })}</span>}
                  <span>{promotion.maxStoredFileSongs == null
                    ? t('account.promotion.audioUnlimited')
                    : t('account.promotion.audioLimit', { count: promotion.maxStoredFileSongs })}</span>
                  <span>{t('account.promotion.activeShares', { count: promotion.activeShareLimit ?? 0 })}</span>
                  {promotion.retentionAmount != null && promotion.retentionUnit && (
                    <span>{t('account.promotion.retention', {
                      count: promotion.retentionAmount,
                      unit: promotion.retentionUnit === 'MONTHS' ? t('account.promotion.unitMonths') : t('account.promotion.unitDays'),
                    })}</span>
                  )}
                  {promotion.shareRetentionAmount != null && promotion.shareRetentionUnit && (
                    <span>{t('account.promotion.shareRetention', {
                      count: promotion.shareRetentionAmount,
                      unit: promotion.shareRetentionUnit === 'MONTHS' ? t('account.promotion.unitMonths') : t('account.promotion.unitDays'),
                    })}</span>
                  )}
                  {promotion.shareDownloadLimit != null && <span>{t('account.promotion.downloadLimit', { count: promotion.shareDownloadLimit })}</span>}
                </div>
              )}
              {!promotionLoading && !promotion?.active && (
                <form className="account-form" onSubmit={(event) => void redeemPromotion(event)}>
                  <label>
                    {t('account.promotion.codeLabel')}
                    <input name="promotionCode" maxLength={40} autoComplete="off" placeholder={t('account.promotion.codePlaceholder')} />
                  </label>
                  <button className="button secondary" type="submit" disabled={promotionSubmitting}>
                    {promotionSubmitting ? t('account.promotion.applying') : t('account.promotion.apply')}
                  </button>
                </form>
              )}
              {promotionMessage && <p className="notice" role="status">{promotionMessage}</p>}
            </section>

            <button className="button secondary" type="button" disabled title={t('account.plan.changeComingSoonTitle')}>
              {t('account.plan.changeComingSoon')}
            </button>
          </section>

          <dl className="account-summary account-summary-minimal">
            <div><dt>{t('account.summary.username')}</dt><dd>{shownProfile.username}</dd></div>
            <div><dt>{t('account.summary.recoveryEmail')}</dt><dd>{shownProfile.recoveryEmailMasked || t('account.recovery.unknown')}</dd></div>
            <div><dt>{t('account.summary.emailVerification')}</dt><dd>{shownProfile.recoveryEmailVerified ? t('account.recovery.verified') : t('account.recovery.verificationRequired')}</dd></div>
          </dl>
          {legalReviewRequired && (
            <section className="account-legal-review" aria-label={t('account.legal.ariaLabel')}>
              <p className="notice warning">{t('account.legal.required')}</p>
              {legalLoading && <p className="muted">{t('account.legal.loading')}</p>}
              {legalError && <p className="notice" role="alert">{legalError}</p>}
              {legalDocuments && (
                <form className="account-form" onSubmit={(event) => void submitLegalReview(event)}>
                  <CentralLegalChecklist title={t('auth.legal.commonTitle')} documents={legalDocuments.commonDocuments} />
                  <CentralLegalChecklist title={t('auth.legal.serviceTitle')} documents={legalDocuments.serviceDocuments} />
                  <label>
                    {t('auth.field.currentPassword')}
                    <input name="password" type="password" required autoComplete="current-password" />
                  </label>
                  <button className="button primary" type="submit" disabled={legalSubmitting || !legalReviewReady()}>
                    {legalSubmitting ? t('common.processing') : t('account.legal.confirmContinue')}
                  </button>
                </form>
              )}
            </section>
          )}
          {status?.demoMode && (
            <p className="notice">{t('account.demo.notice')}</p>
          )}
          <button className="button secondary" onClick={() => void onLogout()}>{t('account.action.logout')}</button>
          {renderNotice('summary')}
        </>
      )}

      {tab === 'profile' && (
        <form
          key={`${shownProfile.authVersion}-${shownProfile.displayName}`}
          className="account-form"
          onSubmit={(event) => void saveProfile(event)}
        >
          <label>
            {t('account.profile.displayName')}
            <input name="displayName" required maxLength={100} defaultValue={shownProfile.displayName} />
          </label>
          <button className="button primary" type="submit" disabled={pendingTab === 'profile'}>{pendingTab === 'profile' ? t('common.saving') : t('account.action.saveDisplayName')}</button>
          <p className="muted">{t('account.profile.scopeNotice')}</p>
          {renderNotice('profile')}
        </form>
      )}

      {tab === 'password' && (
        <form className="account-form" onSubmit={(event) => void changePassword(event)}>
          <label>{t('auth.field.currentPassword')}<input name="currentPassword" type="password" required autoComplete="current-password" /></label>
          <label>{t('auth.field.newPassword')}<input name="newPassword" type="password" required minLength={10} maxLength={100} autoComplete="new-password" /></label>
          <label>{t('auth.field.newPasswordConfirm')}<input name="newPasswordConfirm" type="password" required minLength={10} maxLength={100} autoComplete="new-password" /></label>
          <p className="muted">{t('account.password.rules')}</p>
          <button className="button primary" type="submit" disabled={pendingTab === 'password'}>{pendingTab === 'password' ? t('common.changing') : t('account.action.changePassword')}</button>
          {renderNotice('password')}
        </form>
      )}

      {tab === 'recoveryEmail' && (
        <div className="account-security-section">
          <dl className="account-summary compact">
            <div><dt>{t('account.recovery.currentAddress')}</dt><dd>{shownProfile.recoveryEmailMasked || t('account.recovery.unknown')}</dd></div>
            <div><dt>{t('account.recovery.status')}</dt><dd>{shownProfile.recoveryEmailVerified ? t('account.recovery.verified') : t('account.recovery.verificationRequired')}</dd></div>
          </dl>
          <form className="account-form" onSubmit={(event) => void updateRecoveryEmail(event)}>
            <label>{t('auth.field.currentPassword')}<input name="currentPassword" type="password" required autoComplete="current-password" /></label>
            <label>{t('account.recovery.newEmail')}<input name="recoveryEmail" type="email" required maxLength={320} autoComplete="email" /></label>
            <button className="button primary" type="submit" disabled={pendingTab === 'recoveryEmail'}>{pendingTab === 'recoveryEmail' ? t('common.changing') : t('account.action.changeRecoveryEmail')}</button>
          </form>
          {!shownProfile.recoveryEmailVerified && shownProfile.recoveryEmailMasked && (
            <button className="button secondary" type="button" disabled={pendingTab === 'recoveryEmail'} onClick={() => void resendVerification()}>
              {t('account.action.resendVerification')}
            </button>
          )}
          <p className="muted">{t('account.recovery.resendHint')}</p>
          {renderNotice('recoveryEmail')}
        </div>
      )}
    </section>
  )
}
